import apiClient from './client';
import type { Sector, SectorFormData } from '@/types/sector';
import type { PaginatedResponse } from '@/types/api';
import type { Family } from '@/types/family';

/** Compute display_name from family members if not already set */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeFamilyDisplayName(f: any): Family {
  if (!f.display_name && f.members?.length) {
    const head = f.members.find((m: { family_role?: string }) => m.family_role === 'head');
    if (head?.first_name) {
      f.display_name = `${f.name} (${head.first_name})`;
    }
  }
  if (!f.display_name) f.display_name = f.name;
  return f as Family;
}

export async function getSectors(): Promise<Sector[]> {
  const response = await apiClient.get<{ data: Sector[] } | Sector[]>('/sectors');
  // Backend wraps in { data: [...] }
  return Array.isArray(response.data) ? response.data : response.data.data;
}

export async function getSector(id: string): Promise<Sector> {
  const response = await apiClient.get<{ data: Sector }>(`/sectors/${id}`);
  return response.data.data;
}

export async function createSector(data: SectorFormData): Promise<Sector> {
  const response = await apiClient.post<{ data: Sector }>('/sectors', data);
  return response.data.data;
}

export async function updateSector(id: string, data: Partial<SectorFormData>): Promise<Sector> {
  const response = await apiClient.put<{ data: Sector }>(`/sectors/${id}`, data);
  return response.data.data;
}

export async function deleteSector(id: string): Promise<void> {
  await apiClient.delete(`/sectors/${id}`);
}

export async function getSectorFamilies(
  id: string,
  params?: { page?: number; per_page?: number; sort_by?: string; sort_dir?: 'asc' | 'desc' },
): Promise<PaginatedResponse<Family>> {
  const response = await apiClient.get<PaginatedResponse<Family>>(
    `/sectors/${id}/families`,
    { params },
  );
  const result = response.data;
  result.data = result.data.map(normalizeFamilyDisplayName);
  return result;
}

export async function getSectorsGeoJson(): Promise<GeoJSON.FeatureCollection> {
  const response = await apiClient.get<GeoJSON.FeatureCollection>('/sectors/geojson');
  return response.data;
}

export async function updateSectorGeometry(
  id: string,
  geometry: GeoJSON.Geometry,
): Promise<Sector> {
  const response = await apiClient.put<{ data: Sector }>(`/sectors/${id}/geometry`, { geometry });
  return response.data.data;
}

export async function deleteSectorGeometry(id: string): Promise<void> {
  await apiClient.delete(`/sectors/${id}/geometry`);
}

export interface SectorMembersGroupedResponse {
  families: Family[];
  singles: Array<{
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    birth_date: string | null;
    age: number | null;
    gender: string;
    membership_type: string;
    church_status: string;
    cell_phone: string | null;
    photo_url: string | null;
    gdpr?: boolean | null;
    communion_count?: number | null;
  }>;
  /** Denominator for the per-member communion fraction (X/Y). */
  communion_months?: number;
  counts: {
    total_members: number;
    family_members: number;
    singles: number;
    families: number;
    /**
     * In-evidence people of this sector kept OFF the registry because they are
     * pastoral cases (unbaptized 18+ / prodigal). Surfaced so the removal is
     * never silent.
     */
    excluded_pastoral: number;
  };
}

export async function getSectorMembersGrouped(
  id: string,
  params?: { sort?: 'name' | 'address' },
): Promise<SectorMembersGroupedResponse> {
  const response = await apiClient.get<{ data: SectorMembersGroupedResponse }>(
    `/sectors/${id}/members-grouped`,
    { params },
  );
  const result = response.data.data;
  result.families = result.families.map(normalizeFamilyDisplayName);
  return result;
}

export type SectorExportFormat = 'xlsx' | 'pdf';

const EXPORT_MIME: Record<SectorExportFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};

async function downloadExport(
  url: string,
  params: { sort?: 'name' | 'address' } | undefined,
  format: SectorExportFormat,
  fallbackName: string,
): Promise<void> {
  const response = await apiClient.get(url, { params, responseType: 'blob' });
  const blob = new Blob([response.data], { type: EXPORT_MIME[format] });
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = response.headers['content-disposition']?.match(/filename="?(.+)"?/)?.[1]
    ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export async function exportSectorMembersGrouped(
  id: string,
  params?: { sort?: 'name' | 'address'; format?: SectorExportFormat },
): Promise<void> {
  const format = params?.format ?? 'xlsx';
  await downloadExport(
    `/sectors/${id}/members-grouped/export${format === 'pdf' ? '-pdf' : ''}`,
    { sort: params?.sort },
    format,
    `sector_membri_${new Date().toISOString().slice(0, 10)}.${format}`,
  );
}

/** Every accessible sector's registry in one file (one sheet/page per sector). */
export async function exportAllSectors(
  params?: { sort?: 'name' | 'address'; format?: SectorExportFormat },
): Promise<void> {
  const format = params?.format ?? 'xlsx';
  await downloadExport(
    `/sectors/export${format === 'pdf' ? '-pdf' : ''}`,
    { sort: params?.sort },
    format,
    `sectoare_toate_membri_${new Date().toISOString().slice(0, 10)}.${format}`,
  );
}

export async function autoAssignFamilies(): Promise<{ message: string; affected: number }> {
  const response = await apiClient.post<{ message: string; affected: number }>('/sectors/auto-assign-families');
  return response.data;
}
